import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import {
  Box, Button, Checkbox, FormControl, FormHelperText, FormLabel, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, HStack, Spinner, Text, Textarea, VStack, useColorModeValue,
} from '@chakra-ui/react';
import axios from 'axios';
import { money } from '../utils/money';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// EPIC_VOID_FLOW_2026-10 (item 1571, Chad 2026-10-13: "dialog box please") — the ONE void dialog
// for every document type. A reason is required (the team's notice + the accountant's row carry
// it); "Notify customer" is ticked by default and only offered when the customer actually got the
// document (preview endpoint decides + lists exactly who). The internal reason is never sent to them.
export type VoidDocType = 'Invoice' | 'Proposal' | 'WorkOrder' | 'PurchaseOrder' | 'Bill';

export interface VoidChoice { reason: string; notifyCustomer: boolean; }
// item 1582 (Chad: invoices meant to be written off got VOIDED — and void can't be undone) — an
// optional way out of the dialog: offered ABOVE the reason, it closes without voiding and runs
// the caller's alternative (invoices: "write it off instead").
export interface VoidAlternative { prompt: string; label: string; run: () => void; }
interface OpenArgs { type: VoidDocType; ids: string[]; alternative?: VoidAlternative; }
type OpenFn = (args: OpenArgs) => Promise<VoidChoice | null>;

interface Preview {
  number?: string;
  counterpartyName?: string;
  isVendorFacing: boolean;
  canNotifyCustomer: boolean;
  customerRecipients: string[];
  earners: { name: string; amount: number }[];
  teamCount: number;
  wasEnteredInQuickBooks: boolean;
}

const NOUN: Record<VoidDocType, string> = {
  Invoice: 'invoice',
  Proposal: 'proposal',
  WorkOrder: 'work order',
  PurchaseOrder: 'purchase order',
  Bill: 'bill',
};

// what voiding does beyond the notices, per type (shown so nobody is surprised)
const EFFECT: Partial<Record<VoidDocType, string>> = {
  Invoice: 'Permanent — it can’t be un-voided. Its commissions reverse, the pay link dies, applied credits go back on account, and the work order reopens so a corrected invoice can be generated.',
  Proposal: 'It can be restored later from the proposal’s menu.',
  WorkOrder: 'It can be restored later. A sales order also leaves the customer portal.',
};

const VoidDialogContext = createContext<OpenFn>(async () => null);
export const useVoidDialog = (): OpenFn => useContext(VoidDialogContext);

interface State extends OpenArgs { resolve: (v: VoidChoice | null) => void; }

const VoidDialogProvider: React.FC = ({ children }) => {
  const [state, setState] = useState<State | null>(null);
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const noteBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const altBorder = useColorModeValue('gray.200', 'whiteAlpha.300');

  const open = useCallback<OpenFn>((args) => new Promise<VoidChoice | null>((resolve) => {
    setReason('');
    setNotify(true);
    setPreview(null);
    setState({ ...args, resolve });
  }), []);

  // one document → ask the server who'd be emailed; bulk → no per-doc preview
  useEffect(() => {
    if (!state || state.ids.length !== 1) return undefined;
    let live = true;
    setLoading(true);
    axios.get(`${apiUrl()}DocumentVoid/preview`, { params: { type: state.type, id: state.ids[0] } })
      .then((r) => { if (live) setPreview(r.data as Preview); })
      .catch(() => { if (live) setPreview(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [state]);

  const close = (v: VoidChoice | null) => {
    state?.resolve(v);
    setState(null);
  };

  if (!state) return <VoidDialogContext.Provider value={open}>{children}</VoidDialogContext.Provider>;

  const noun = NOUN[state.type];
  const bulk = state.ids.length > 1;
  const party = preview?.isVendorFacing || state.type === 'PurchaseOrder' ? 'vendor' : 'customer';
  const title = bulk ? `Void ${state.ids.length} ${noun}s` : `Void ${preview?.number || `this ${noun}`}`;
  const showNotify = bulk ? state.type !== 'Bill' : !!preview?.canNotifyCustomer;
  const valid = reason.trim().length > 0;
  const earnerCount = preview?.earners?.length ?? 0;

  return (
    <VoidDialogContext.Provider value={open}>
      {children}
      <Modal isOpen onClose={() => close(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {EFFECT[state.type] && <Text fontSize="sm" color={sub}>{EFFECT[state.type]}</Text>}
              {state.alternative && (
                <HStack
                  spacing={3}
                  justify="space-between"
                  borderWidth="1px"
                  borderColor={altBorder}
                  borderRadius="md"
                  px={3}
                  py={2}
                >
                  <Text fontSize="sm">{state.alternative.prompt}</Text>
                  <Button
                    size="sm"
                    variant="outline"
                    flexShrink={0}
                    onClick={() => { const alt = state.alternative; close(null); alt?.run(); }}
                  >
                    {state.alternative.label}
                  </Button>
                </HStack>
              )}
              <FormControl isRequired>
                <FormLabel>Reason</FormLabel>
                <Textarea
                  autoFocus
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={`Why this ${noun} is being voided`}
                />
                <FormHelperText>{`Goes to your team and the accountant. Never to the ${party}.`}</FormHelperText>
              </FormControl>

              {loading && <Spinner size="sm" />}

              {showNotify && (
                <Box>
                  <Checkbox isChecked={notify} onChange={(e) => setNotify(e.target.checked)}>
                    {bulk ? `Notify the ${party}s who received them` : `Notify the ${party}`}
                  </Checkbox>
                  <Text fontSize="xs" color={sub} mt={1} pl={6}>
                    {bulk
                      ? `Only ${party}s who actually received the ${noun} are emailed — a short notice that it's void, no reason.`
                      : `${(preview?.customerRecipients ?? []).join(', ')} — a short notice that it's void, no reason.`}
                  </Text>
                </Box>
              )}
              {!bulk && preview && !preview.canNotifyCustomer && state.type !== 'Bill' && state.type !== 'WorkOrder' && (
                <Text fontSize="sm" color={sub}>{`The ${party} never received this ${noun}, so they won't be emailed.`}</Text>
              )}

              <Box bg={noteBg} borderRadius="md" px={3} py={2}>
                <Text fontSize="sm">
                  {`Your team is emailed the reason: admins and accountants${earnerCount > 0 ? `, plus ${earnerCount} commission earner${earnerCount === 1 ? '' : 's'}` : ''}.`}
                </Text>
                {earnerCount > 0 && state.type === 'Invoice' && (
                  <Text fontSize="xs" color={sub} mt={1}>
                    {`Reverses ${preview!.earners.map((e) => `${e.name} ${money(e.amount)}`).join(' · ')}`}
                  </Text>
                )}
                {preview?.wasEnteredInQuickBooks && (
                  <Text fontSize="xs" color={sub} mt={1}>Already in QuickBooks — the accountant gets a row to void it there too.</Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => close(null)}>Cancel</Button>
            <Button
              colorScheme="red"
              isDisabled={!valid}
              onClick={() => close({ reason: reason.trim(), notifyCustomer: showNotify && notify })}
            >
              {bulk ? `Void ${state.ids.length}` : 'Void'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VoidDialogContext.Provider>
  );
};

export default VoidDialogProvider;
